import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TestingHqContext } from '../../context/TestingHqContext';
import { UserContext } from '../../context/UserContext';

import { get_testing_accept_count, get_testing_list, get_testing_place_list, get_testing_schedule_list, get_testing_type } from '../../utils/restapi';
import TESTING_ACCEPT_STATUS from '../../constants/testing_accept';

import './stat.css';

const AUTO_REFRESH_MS = 10000;
const REQUEST_STATUS = TESTING_ACCEPT_STATUS.field.join;        // 1: 접수신청
const ACCEPT_COMPLETE_STATUS = TESTING_ACCEPT_STATUS.field.decided; // 2: 접수 및 결제완료

const EMPTY_SNAPSHOT = { rows: new Map(), summary: { requestCount: 0, acceptCompleteCount: 0, totalCount: 0 } };

const TestingAcceptStat = () => {
  const { testingHqs } = useContext(TestingHqContext);
  const { user } = useContext(UserContext);

  // 이 페이지가 자체적으로 고정하는 "이번 회차"
  const [roundObj, setRoundObj] = useState(null);
  const [isResolvingRound, setIsResolvingRound] = useState(true);

  const [testingPlaces, setTestingPlaces] = useState([]);
  const [selectedPlaceId, setSelectedPlaceId] = useState('');

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState('');

  const [refreshToken, setRefreshToken] = useState(0);
  const [countdown, setCountdown] = useState(Math.floor(AUTO_REFRESH_MS / 1000));
  const previousSnapshotRef = useRef(EMPTY_SNAPSHOT);

  const myHqCodes = testingHqs.map((hq) => hq.code).filter(Boolean);
  const myHqCodesKey = myHqCodes.join(',');

  // ── "이번 회차" 결정 (testing_place/joins 와 동일한 방식) ──────────────────────
  //  1) '정기' 시험 유형을 이름으로 찾는다
  //  2) 해당 유형의 회차 목록을 가져온다
  //  3) 각 회차의 시험일정으로 진행중 > 임박예정 > 최근종료 순으로 1건 선택
  useEffect(() => {
    let cancelled = false;

    const resolveCurrentRound = async () => {
      setIsResolvingRound(true);
      try {
        const types = await get_testing_type();
        const typeList = types || [];

        const regularType = typeList.find((type) => {
          const name = `${type?.nickname || ''}${type?.name || ''}`;
          return name.includes('정기');
        });
        if (!regularType?.code) {
          if (!cancelled) setErrorMessage('정기시험 유형을 찾지 못했습니다.');
          return;
        }

        const testingResponse = await get_testing_list({ type: regularType.code, page: 1, rowsPerPage: 50, sort: 'a_testing_id', desc: 'desc' });
        const regularTestings = testingResponse?.lists || testingResponse || [];
        if (!regularTestings.length) {
          if (!cancelled) setErrorMessage('정기시험 회차가 없습니다.');
          return;
        }
        if (cancelled) return;

        const regularTypeName = regularType.nickname || regularType.name || '';

        // 회차 목록을 받는 즉시 최신 회차를 표시(멈춤 해소). 정밀 선택은 아래 일정조회로 갱신.
        setRoundObj({ ...regularTestings[0], type: regularType.code, typeName: regularTypeName });
        setErrorMessage('');
        setIsResolvingRound(false);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const parseDate = (value) => {
          if (!value) return null;
          const normalized = String(value).replace(/\./g, '-').replace(/\//g, '-').trim();
          const date = new Date(normalized);
          if (Number.isNaN(date.getTime())) return null;
          date.setHours(0, 0, 0, 0);
          return date;
        };

        // 1) 진행 중(시작일 <= 오늘 <= 종료일)  2) 임박한 예정  3) 최근 종료
        // 현재 회차는 최신 쪽에 있으므로 최신 15개만 확인하고, 진행중을 찾으면 조기 종료(성능).
        let inProgress = null;
        let nearestUpcoming = null;
        let latestPast = null;
        const candidates = regularTestings.slice(0, 15);

        for (const testing of candidates) {
          if (cancelled) return;
          if (inProgress) break;
          try {
            const scheduleResponse = await get_testing_schedule_list({
              testing_id: testing.testing_id,
              page: 1,
              rowsPerPage: 1000,
              sort: 'a_testStopDate',
              desc: 'desc',
            });

            const schedules = scheduleResponse?.lists || scheduleResponse || [];
            for (const schedule of schedules) {
              const startDate = parseDate(schedule?.testStartDate || schedule?.startDate || schedule?.testStopDate || schedule?.stopDate);
              const stopDate = parseDate(schedule?.testStopDate || schedule?.stopDate || schedule?.testStartDate || schedule?.startDate);
              if (!startDate || !stopDate) continue;

              const isInProgress = startDate <= today && today <= stopDate;
              if (isInProgress) {
                if (!inProgress || stopDate < inProgress.date) inProgress = { testing, date: stopDate };
                continue;
              }

              if (stopDate >= today) {
                if (!nearestUpcoming || stopDate < nearestUpcoming.date) nearestUpcoming = { testing, date: stopDate };
              } else {
                if (!latestPast || stopDate > latestPast.date) latestPast = { testing, date: stopDate };
              }
            }
          } catch (error) {
            console.error('Error fetching testing schedule:', error);
          }
        }

        const selected = inProgress?.testing || nearestUpcoming?.testing || latestPast?.testing || regularTestings[0];
        if (!cancelled && selected) {
          setRoundObj({ ...selected, type: regularType.code, typeName: regularTypeName });
          setErrorMessage('');
        }
      } catch (error) {
        console.error('Error resolving current round:', error);
        if (!cancelled) setErrorMessage(`이번 회차 정보를 불러오지 못했습니다. (${error?.message || '네트워크 오류'})`);
      } finally {
        if (!cancelled) setIsResolvingRound(false);
      }
    };

    resolveCurrentRound();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 고사장 필터용 목록 (회차/소속본부 변경 시 갱신) ───────────────────────────
  useEffect(() => {
    let cancelled = false;

    const loadPlaces = async () => {
      if (!roundObj?.testing_id || !myHqCodes.length) {
        setTestingPlaces([]);
        return;
      }
      try {
        const response = await get_testing_place_list({
          person_id: user?.person_id || 0,
          testing_id: Number(roundObj.testing_id),
          gcodes: myHqCodes.join(','),
          page: 1,
          rowsPerPage: 10000,
          sort: 'a_gcode,a_code',
          isAll: false,
        });
        if (cancelled) return;
        setTestingPlaces(response?.lists || []);
      } catch (error) {
        console.error('Error fetching testing places:', error);
        if (!cancelled) setTestingPlaces([]);
      }
    };

    loadPlaces();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundObj?.testing_id, myHqCodesKey]);

  // 선택된 고사장이 현재 목록에 없으면 초기화
  useEffect(() => {
    if (selectedPlaceId && !testingPlaces.some((p) => String(p.testing_place_id) === String(selectedPlaceId))) {
      setSelectedPlaceId('');
    }
  }, [testingPlaces, selectedPlaceId]);

  // ── 접수현황 집계 ────────────────────────────────────────────────────────────
  const refreshStats = async () => {
    if (!roundObj?.testing_id) {
      setRows([]);
      setLastUpdatedAt('');
      setErrorMessage('');
      return;
    }
    if (!myHqCodes.length) {
      setRows([]);
      setLastUpdatedAt('');
      setErrorMessage('소속 지역본부 정보가 없습니다.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const placeParams = {
        person_id: user?.person_id || 0,
        testing_id: Number(roundObj.testing_id),
        gcodes: myHqCodes.join(','),
        page: 1,
        rowsPerPage: 10000,
        sort: 'a_gcode,a_code',
        isAll: false,
      };

      // 고사장 목록과 접수인원 카운트를 병렬 조회.
      // 카운트는 전용 엔드포인트(GROUP BY 집계)로 한 번에 — getMap 없이 빠르고 안전.
      // ※ 카운트는 지역본부(hcodes)로 거르지 않는다: 해당 고사장으로 접수된 "전원"을 집계해야 한다.
      //   (표시 대상 고사장만 내 지역본부 소속으로 제한 → 아래 allowedPlaceIds 로 매핑)
      const [placeResponse, countResponse] = await Promise.all([
        get_testing_place_list(placeParams),
        get_testing_accept_count({
          type: Number(roundObj.type),
          testing_id: Number(roundObj.testing_id),
        }),
      ]);

      const places = placeResponse?.lists || [];
      const counts = countResponse?.lists || [];

      const allowedPlaceIds = new Set(places.map((p) => Number(p.testing_place_id)).filter(Boolean));
      const rowMap = new Map();
      // 고사장 목록 기준으로 0명 고사장도 표시
      places.forEach((place) => {
        const placeId = place.testing_place_id;
        if (!placeId) return;
        rowMap.set(placeId, { testing_place_id: placeId, testingPlaceName: place.name || '-', requestCount: 0, acceptCompleteCount: 0, totalCount: 0 });
      });
      // 카운트 행: { testing_place_id, status, cnt } → 고사장별·상태별 합산
      // (접수신청=상태1, 접수완료=상태2 이상, 전체=모든 유효 접수)
      counts.forEach((c) => {
        const placeId = c.testing_place_id;
        if (!placeId || !allowedPlaceIds.has(Number(placeId))) return;
        const row = rowMap.get(placeId);
        if (!row) return;
        const status = Number(c.status);
        const cnt = Number(c.cnt) || 0;
        if (status === REQUEST_STATUS) row.requestCount += cnt;
        else if (status >= ACCEPT_COMPLETE_STATUS) row.acceptCompleteCount += cnt;
        row.totalCount += cnt;
      });

      const nextRows = Array.from(rowMap.values())
        .map((row) => {
          const prevRow = previousSnapshotRef.current.rows.get(row.testing_place_id);
          return {
            ...row,
            requestDelta: row.requestCount - (prevRow?.requestCount || 0),
            acceptCompleteDelta: row.acceptCompleteCount - (prevRow?.acceptCompleteCount || 0),
            totalDelta: row.totalCount - (prevRow?.totalCount || 0),
          };
        })
        .sort((a, b) => (a.testingPlaceName || '').localeCompare(b.testingPlaceName || '', 'ko'));

      previousSnapshotRef.current = {
        rows: new Map(nextRows.map((row) => [row.testing_place_id, {
          requestCount: row.requestCount,
          acceptCompleteCount: row.acceptCompleteCount,
          totalCount: row.totalCount,
        }])),
        summary: { requestCount: 0, acceptCompleteCount: 0, totalCount: 0 },
      };

      setRows(nextRows);
      setLastUpdatedAt(new Date().toLocaleString('ko-KR'));
      setCountdown(Math.floor(AUTO_REFRESH_MS / 1000));
    } catch (error) {
      console.error('Error fetching status data:', error);
      setRows([]);
      setErrorMessage('접수 현황을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 회차/소속본부 변경 시 스냅샷 초기화 후 즉시 갱신
  useEffect(() => {
    setRows([]);
    previousSnapshotRef.current = EMPTY_SNAPSHOT;
    setLastUpdatedAt('');
    setErrorMessage('');
    setRefreshToken((prev) => prev + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundObj?.testing_id, myHqCodesKey]);

  useEffect(() => {
    refreshStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundObj?.testing_id, myHqCodesKey, refreshToken]);

  // 자동 새로고침 + 카운트다운
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      setRefreshToken((prev) => prev + 1);
      setCountdown(Math.floor(AUTO_REFRESH_MS / 1000));
    }, AUTO_REFRESH_MS);

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? Math.floor(AUTO_REFRESH_MS / 1000) : prev - 1));
    }, 1000);

    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, []);

  // 고사장 필터 적용된 표시 행
  const displayedRows = useMemo(() => {
    if (!selectedPlaceId) return rows;
    return rows.filter((row) => String(row.testing_place_id) === String(selectedPlaceId));
  }, [rows, selectedPlaceId]);

  // 표시 행 기준 합계 (필터 반영)
  const summary = useMemo(() => displayedRows.reduce(
    (acc, row) => {
      acc.requestCount += row.requestCount;
      acc.acceptCompleteCount += row.acceptCompleteCount;
      acc.totalCount += row.totalCount;
      acc.requestDelta += row.requestDelta;
      acc.acceptCompleteDelta += row.acceptCompleteDelta;
      acc.totalDelta += row.totalDelta;
      return acc;
    },
    { requestCount: 0, acceptCompleteCount: 0, totalCount: 0, requestDelta: 0, acceptCompleteDelta: 0, totalDelta: 0 }
  ), [displayedRows]);

  const formatDelta = (value) => {
    if (!value) return '0';
    return value > 0 ? `+${value}` : `${value}`;
  };

  const getDeltaClassName = (value) => {
    if (value > 0) return 'stat_delta up';
    if (value < 0) return 'stat_delta down';
    return 'stat_delta';
  };

  const handlePlaceChange = (e) => setSelectedPlaceId(e.target.value);

  const handleManualRefresh = () => {
    setRefreshToken((prev) => prev + 1);
    setCountdown(Math.floor(AUTO_REFRESH_MS / 1000));
  };

  return (
    <div id="testing_accept_stat">
      <div className="head">
        <h1>이번회차 고사장별 접수현황</h1>
      </div>
      <div className="body">
        <div className="top">
          <ul className="form">
            <li>
              <label>이번 회차</label>
              <span className="current_round">
                {roundObj
                  ? `${roundObj.typeName ? roundObj.typeName + ' ' : ''}${roundObj.title || ''}`.trim() || '-'
                  : (isResolvingRound ? '회차 정보를 불러오는 중...' : '표시할 회차가 없습니다')}
              </span>
            </li>
            <li>
              <label>고사장</label>
              <span>
                <select value={selectedPlaceId} onChange={handlePlaceChange}>
                  <option value="">▒ 전체 고사장 ▒</option>
                  {testingPlaces.map((obj, index) => (
                    <option key={index} value={obj.testing_place_id}>
                      {obj.name}
                    </option>
                  ))}
                </select>
              </span>
            </li>
            <li>
              <button type="button" className="refresh_btn" onClick={handleManualRefresh}>
                지금 새로고침 ({countdown}초)
              </button>
            </li>
          </ul>
        </div>

        <div className="stat_meta">
          <p>고사장 범위: 내 계정 관리 본부 {myHqCodes.length}개 / 고사장 {testingPlaces.length}곳</p>
          <p>마지막 갱신: {lastUpdatedAt || '-'}</p>
        </div>

        <div className="stat_summary">
          <div className="summary_card">
            <strong>접수신청</strong>
            <span>{summary.requestCount}</span>
            <em className={getDeltaClassName(summary.requestDelta)}>({formatDelta(summary.requestDelta)})</em>
          </div>
          <div className="summary_card">
            <strong>접수완료</strong>
            <span>{summary.acceptCompleteCount}</span>
            <em className={getDeltaClassName(summary.acceptCompleteDelta)}>({formatDelta(summary.acceptCompleteDelta)})</em>
          </div>
          <div className="summary_card">
            <strong>전체(1+2)</strong>
            <span>{summary.totalCount}</span>
            <em className={getDeltaClassName(summary.totalDelta)}>({formatDelta(summary.totalDelta)})</em>
          </div>
        </div>

        <div className="middle stat_table_wrap">
          {errorMessage ? <p className="stat_error">{errorMessage}</p> : null}

          <table className="stat_table">
            <thead>
              <tr>
                <th>NO.</th>
                <th>고사장</th>
                <th>접수신청(1)</th>
                <th>접수완료(2)</th>
                <th>전체(1+2)</th>
                <th>증감(10초 전 대비)</th>
              </tr>
            </thead>
            <tbody>
              {!displayedRows.length ? (
                <tr>
                  <td colSpan={6} className="stat_empty">
                    {isLoading ? '데이터를 불러오는 중입니다.' : '조회된 데이터가 없습니다.'}
                  </td>
                </tr>
              ) : null}

              {displayedRows.map((row, index) => (
                <tr key={row.testing_place_id}>
                  <td>{index + 1}</td>
                  <td>{row.testingPlaceName || '-'}</td>
                  <td>{row.requestCount}</td>
                  <td>{row.acceptCompleteCount}</td>
                  <td>{row.totalCount}</td>
                  <td>
                    <span className={getDeltaClassName(row.requestDelta)}>신청:{formatDelta(row.requestDelta)}</span>
                    {' / '}
                    <span className={getDeltaClassName(row.acceptCompleteDelta)}>완료:{formatDelta(row.acceptCompleteDelta)}</span>
                    {' / '}
                    <span className={getDeltaClassName(row.totalDelta)}>전체:{formatDelta(row.totalDelta)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="stat_total_row">
                <td colSpan={2}>전체 합계</td>
                <td>{summary.requestCount}</td>
                <td>{summary.acceptCompleteCount}</td>
                <td>{summary.totalCount}</td>
                <td>
                  <span className={getDeltaClassName(summary.requestDelta)}>신청:{formatDelta(summary.requestDelta)}</span>
                  {' / '}
                  <span className={getDeltaClassName(summary.acceptCompleteDelta)}>완료:{formatDelta(summary.acceptCompleteDelta)}</span>
                  {' / '}
                  <span className={getDeltaClassName(summary.totalDelta)}>전체:{formatDelta(summary.totalDelta)}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TestingAcceptStat;
